import { EventCategory } from "@/types/ui";
import { FrameName } from "..";
import { ResourceType } from "../resources";
import { EventUpgradeIds } from "./ids";
import type { UpgradeItem } from "./index";

export { EventUpgradeIds };

export type EventUpgradeId = number;

export const eventUpgrades = new Map<EventUpgradeId, UpgradeItem>([
	// -------- CHRISTMAS UPGRADES START HERE --------
	[
		EventUpgradeIds.ch_bow_saw,
		{
			levelUnlock: 1,
			heading: "upgrade.bow_saw",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.DARKWOOD_LUMBER_MILL}.name`,
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.EVENTIUM]: 1_000 },
				2: { [ResourceType.EVENTIUM]: 30_000 },
				3: { [ResourceType.EVENTIUM]: 60_000 },
				4: { [ResourceType.EVENTIUM]: 100_000 },
				5: { [ResourceType.EVENTIUM]: 150_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.CHRISTMAS,
		},
	],
	[
		EventUpgradeIds.ch_two_man_saw,
		{
			levelUnlock: 1,
			heading: "upgrade.two_man_saw",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.DARKWOOD_LUMBER_MILL}.name`,
				"{percent}": "20%",
			},
			replaceValue: 0.2,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 250_000,
					[ResourceType.GEMS]: 100,
				},
				2: {
					[ResourceType.EVENTIUM]: 500_000,
					[ResourceType.GEMS]: 100,
				},
				3: {
					[ResourceType.EVENTIUM]: 750_000,
					[ResourceType.GEMS]: 100,
				},
				4: {
					[ResourceType.EVENTIUM]: 1_000_000,
					[ResourceType.GEMS]: 100,
				},
				5: {
					[ResourceType.EVENTIUM]: 1_500_000,
					[ResourceType.GEMS]: 100,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.CHRISTMAS,
		},
	],
	[
		EventUpgradeIds.ch_wood_splitting,
		{
			levelUnlock: 1,
			heading: "upgrade.wood_splitting",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.DARKWOOD_LUMBER_MILL}.name`,
				"{percent}": "50%",
			},
			replaceValue: 0.5,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 2_500_000,
					[ResourceType.GEMS]: 250,
				},
				2: {
					[ResourceType.EVENTIUM]: 10_000_000,
					[ResourceType.GEMS]: 250,
				},
			},
			maxLevel: 2,
			eventType: EventCategory.CHRISTMAS,
		},
	],
	[
		EventUpgradeIds.ch_wood_processing,
		{
			levelUnlock: 1,
			heading: "upgrade.wood_processing",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.DARKWOOD_LUMBER_MILL}.name`,
				"{percent}": "100%",
			},
			replaceValue: 1.0,
			price: {
				1: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 1,
			eventType: EventCategory.CHRISTMAS,
		},
	],
	[
		EventUpgradeIds.ch_beasts_of_burden,
		{
			levelUnlock: 1,
			heading: "upgrade.beasts_of_burden",
			description: "upgrade.effective_range_description",
			descriptionReplace: {
				"{building}": `${FrameName.DARKWOOD_LUMBER_MILL}.name`,
				"{number}": "1",
			},
			replaceValue: 1, // +1 reach
			price: {
				1: { [ResourceType.GEMS]: 750 },
			},
			maxLevel: 1,
			eventType: EventCategory.CHRISTMAS,
		},
	],
	[
		EventUpgradeIds.ch_log_driving,
		{
			levelUnlock: 1,
			heading: "upgrade.log_driving",
			description: "upgrade.no_road_required_description",
			descriptionReplace: {
				"{building}": `${FrameName.DARKWOOD_LUMBER_MILL}.name`,
			},
			replaceValue: 1, // boolean-like toggle
			price: {
				1: {
					[ResourceType.EVENTIUM]: 5_000_000,
					[ResourceType.GEMS]: 500,
				},
			},
			maxLevel: 1,
			eventType: EventCategory.CHRISTMAS,
		},
	],
	[
		EventUpgradeIds.ch_mountain_lumberjacks,
		{
			levelUnlock: 1,
			heading: "upgrade.mountain_lumberjacks",
			description: "upgrade.mountain_lumberjacks_description",
			descriptionReplace: {
				"{building}": `${FrameName.DARKWOOD_LUMBER_MILL}.name`,
			},
			replaceValue: 1, // enable capability
			price: {
				1: { [ResourceType.EVENTIUM]: 10_000_000 },
			},
			maxLevel: 1,
			eventType: EventCategory.CHRISTMAS,
		},
	],
	[
		EventUpgradeIds.ch_highlanders,
		{
			levelUnlock: 1,
			heading: "upgrade.highlanders",
			description: "upgrade.mountain_hill_building_description",
			descriptionReplace: {
				"{building}": `${FrameName.DARKWOOD_LUMBER_MILL}.name`,
			},
			replaceValue: 1, // enable placement
			price: {
				1: { [ResourceType.EVENTIUM]: 1_000_000 },
			},
			maxLevel: 1,
			eventType: EventCategory.CHRISTMAS,
		},
	],
	[
		EventUpgradeIds.ch_wood_carving,
		{
			levelUnlock: 1,
			heading: "upgrade.wood_carving",
			description: "upgrade.extra_coin_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.DARKWOOD_LUMBER_MILL}.name`,
				"{percent}": "50%",
			},
			replaceValue: 0.5,
			price: {
				1: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 1,
			eventType: EventCategory.CHRISTMAS,
		},
	],
	[
		EventUpgradeIds.ch_trading,
		{
			levelUnlock: 1,
			heading: "upgrade.trading",
			description: "event.christmas_event.conversion_rate_description",
			descriptionReplace: {
				"{percent}": "2%",
			},
			replaceValue: 0.02,
			price: {
				1: { [ResourceType.EVENTIUM]: 1_000_000 },
				2: { [ResourceType.EVENTIUM]: 2_000_000 },
				3: { [ResourceType.EVENTIUM]: 3_000_000 },
				4: { [ResourceType.EVENTIUM]: 4_000_000 },
				5: { [ResourceType.EVENTIUM]: 5_000_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.CHRISTMAS,
		},
	],
	[
		EventUpgradeIds.ch_foreman,
		{
			levelUnlock: 1,
			heading: "upgrade.foreman",
			description: "upgrade.darkwood_offline_income_description",
			descriptionReplace: {
				"{percent}": "100%",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 1,
			eventType: EventCategory.CHRISTMAS,
		},
	],
	// -------- EASTER UPGRADES START HERE --------
	[
		EventUpgradeIds.ea_egg_painting,
		{
			levelUnlock: 1,
			heading: "upgrade.egg_painting",
			description: "upgrade.easter_egg_production_description",
			descriptionReplace: {
				"{number}": "2",
			},
			replaceValue: 2,
			price: {
				1: { [ResourceType.EVENTIUM]: 10 },
				2: { [ResourceType.EVENTIUM]: 25 },
				3: { [ResourceType.EVENTIUM]: 40 },
				4: { [ResourceType.EVENTIUM]: 80 },
				5: { [ResourceType.EVENTIUM]: 160 },
			},
			maxLevel: 5,
			eventType: EventCategory.EASTER,
		},
	],
	[
		EventUpgradeIds.ea_trained_decorators,
		{
			levelUnlock: 1,
			heading: "upgrade.trained_decorators",
			description: "upgrade.easter_egg_production_description",
			descriptionReplace: {
				"{number}": "4",
			},
			replaceValue: 4,
			price: {
				1: { [ResourceType.EVENTIUM]: 1_000, [ResourceType.GEMS]: 200 },
				2: { [ResourceType.EVENTIUM]: 2_500, [ResourceType.GEMS]: 200 },
				3: { [ResourceType.EVENTIUM]: 4_000, [ResourceType.GEMS]: 200 },
				4: { [ResourceType.EVENTIUM]: 8_000, [ResourceType.GEMS]: 200 },
				5: {
					[ResourceType.EVENTIUM]: 16_000,
					[ResourceType.GEMS]: 200,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.EASTER,
		},
	],
	[
		EventUpgradeIds.ea_artistic_decorators,
		{
			levelUnlock: 1,
			heading: "upgrade.artistic_decorators",
			description: "upgrade.easter_egg_production_description",
			descriptionReplace: {
				"{number}": "6",
			},
			replaceValue: 6,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 50_000,
					[ResourceType.GEMS]: 400,
				},
				2: {
					[ResourceType.EVENTIUM]: 65_000,
					[ResourceType.GEMS]: 400,
				},
				3: {
					[ResourceType.EVENTIUM]: 80_000,
					[ResourceType.GEMS]: 400,
				},
				4: {
					[ResourceType.EVENTIUM]: 160_000,
					[ResourceType.GEMS]: 400,
				},
				5: {
					[ResourceType.EVENTIUM]: 320_000,
					[ResourceType.GEMS]: 400,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.EASTER,
		},
	],
	[
		EventUpgradeIds.ea_easter_workshops,
		{
			levelUnlock: 1,
			heading: "upgrade.easter_workshops",
			description: "upgrade.easter_egg_production_description",
			descriptionReplace: {
				"{number}": "15",
			},
			replaceValue: 15,
			price: {
				1: { [ResourceType.GEMS]: 1_000 },
				2: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 2,
			eventType: EventCategory.EASTER,
		},
	],
	[
		EventUpgradeIds.ea_small_surprise,
		{
			levelUnlock: 1,
			heading: "upgrade.small_surprise",
			description: "upgrade.easter_egg_reward_description",
			descriptionReplace: {
				"{number}": "2",
			},
			replaceValue: 2,
			price: {
				1: { [ResourceType.EVENTIUM]: 15 },
				2: { [ResourceType.EVENTIUM]: 30 },
				3: { [ResourceType.EVENTIUM]: 100 },
				4: { [ResourceType.EVENTIUM]: 200 },
			},
			maxLevel: 4,
			eventType: EventCategory.EASTER,
		},
	],
	[
		EventUpgradeIds.ea_medium_surprise,
		{
			levelUnlock: 1,
			heading: "upgrade.medium_surprise",
			description: "upgrade.easter_egg_reward_description",
			descriptionReplace: {
				"{number}": "4",
			},
			replaceValue: 4,
			price: {
				1: { [ResourceType.EVENTIUM]: 500, [ResourceType.GEMS]: 200 },
				2: { [ResourceType.EVENTIUM]: 650, [ResourceType.GEMS]: 200 },
				3: { [ResourceType.EVENTIUM]: 800, [ResourceType.GEMS]: 200 },
				4: { [ResourceType.EVENTIUM]: 1_600, [ResourceType.GEMS]: 200 },
				5: { [ResourceType.EVENTIUM]: 3_200, [ResourceType.GEMS]: 200 },
			},
			maxLevel: 5,
			eventType: EventCategory.EASTER,
		},
	],
	[
		EventUpgradeIds.ea_big_surprise,
		{
			levelUnlock: 1,
			heading: "upgrade.big_surprise",
			description: "upgrade.easter_egg_reward_description",
			descriptionReplace: {
				"{number}": "6",
			},
			replaceValue: 6,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 75_000,
					[ResourceType.GEMS]: 400,
				},
				2: {
					[ResourceType.EVENTIUM]: 90_000,
					[ResourceType.GEMS]: 400,
				},
				3: {
					[ResourceType.EVENTIUM]: 135_000,
					[ResourceType.GEMS]: 400,
				},
				4: {
					[ResourceType.EVENTIUM]: 270_000,
					[ResourceType.GEMS]: 400,
				},
				5: {
					[ResourceType.EVENTIUM]: 540_000,
					[ResourceType.GEMS]: 400,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.EASTER,
		},
	],
	[
		EventUpgradeIds.ea_giant_surprise,
		{
			levelUnlock: 1,
			heading: "upgrade.giant_surprise",
			description: "upgrade.easter_egg_reward_description",
			descriptionReplace: {
				"{number}": "10",
			},
			replaceValue: 10,
			price: {
				1: { [ResourceType.GEMS]: 1_000 },
				2: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 2,
			eventType: EventCategory.EASTER,
		},
	],
	[
		EventUpgradeIds.ea_golden_chance,
		{
			levelUnlock: 1,
			heading: "upgrade.golden_chance",
			description: "upgrade.golden_egg_drop_rate_description",
			descriptionReplace: {
				"{percent}": "1%",
			},
			replaceValue: 0.01,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 8_000,
					[ResourceType.GEMS]: 400,
				},
				2: {
					[ResourceType.EVENTIUM]: 24_000,
					[ResourceType.GEMS]: 400,
				},
				3: {
					[ResourceType.EVENTIUM]: 72_000,
					[ResourceType.GEMS]: 400,
				},
				4: {
					[ResourceType.EVENTIUM]: 216_000,
					[ResourceType.GEMS]: 400,
				},
				5: {
					[ResourceType.EVENTIUM]: 648_000,
					[ResourceType.GEMS]: 400,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.EASTER,
		},
	],
	[
		EventUpgradeIds.ea_golden_jackpot,
		{
			levelUnlock: 1,
			heading: "upgrade.golden_jackpot",
			description: "upgrade.golden_egg_drop_rate_description",
			descriptionReplace: {
				"{percent}": "2%",
			},
			replaceValue: 0.02,
			price: {
				1: { [ResourceType.GEMS]: 750 },
				2: { [ResourceType.GEMS]: 750 },
			},
			maxLevel: 2,
			eventType: EventCategory.EASTER,
		},
	],
	[
		EventUpgradeIds.ea_golden_surprise,
		{
			levelUnlock: 1,
			heading: "upgrade.golden_surprise",
			description: "upgrade.golden_egg_reward_description",
			descriptionReplace: {
				"{number}": "20",
			},
			replaceValue: 20,
			price: {
				1: { [ResourceType.EVENTIUM]: 1_000, [ResourceType.GEMS]: 200 },
				2: { [ResourceType.EVENTIUM]: 3_000, [ResourceType.GEMS]: 200 },
				3: { [ResourceType.EVENTIUM]: 5_000, [ResourceType.GEMS]: 200 },
				4: {
					[ResourceType.EVENTIUM]: 10_000,
					[ResourceType.GEMS]: 200,
				},
				5: {
					[ResourceType.EVENTIUM]: 20_000,
					[ResourceType.GEMS]: 200,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.EASTER,
		},
	],
	[
		EventUpgradeIds.ea_golden_surprise2,
		{
			levelUnlock: 1,
			heading: ["upgrade.golden_surprise", "upgrade.2.non_translatable"],
			description: "upgrade.golden_egg_reward_description",
			descriptionReplace: {
				"{number}": "50",
			},
			replaceValue: 50,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 120_000,
					[ResourceType.GEMS]: 400,
				},
				2: {
					[ResourceType.EVENTIUM]: 150_000,
					[ResourceType.GEMS]: 400,
				},
				3: {
					[ResourceType.EVENTIUM]: 180_000,
					[ResourceType.GEMS]: 400,
				},
				4: {
					[ResourceType.EVENTIUM]: 210_000,
					[ResourceType.GEMS]: 400,
				},
				5: {
					[ResourceType.EVENTIUM]: 240_000,
					[ResourceType.GEMS]: 400,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.EASTER,
		},
	],
	[
		EventUpgradeIds.ea_golden_surprise3,
		{
			levelUnlock: 1,
			heading: ["upgrade.golden_surprise", "upgrade.3.non_translatable"],
			description: "upgrade.golden_egg_reward_description",
			descriptionReplace: {
				"{number}": "100",
			},
			replaceValue: 100,
			price: {
				1: { [ResourceType.GEMS]: 1_000 },
				2: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 2,
			eventType: EventCategory.EASTER,
		},
	],
	[
		EventUpgradeIds.ea_easter_bunny,
		{
			levelUnlock: 1,
			heading: "upgrade.easter_bunny",
			description: "upgrade.easter_egg_cap_description",
			descriptionReplace: {
				"{number}": "2",
			},
			replaceValue: 2,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 10_000,
					[ResourceType.GEMS]: 400,
				},
				2: {
					[ResourceType.EVENTIUM]: 15_000,
					[ResourceType.GEMS]: 400,
				},
				3: {
					[ResourceType.EVENTIUM]: 20_000,
					[ResourceType.GEMS]: 400,
				},
				4: {
					[ResourceType.EVENTIUM]: 25_000,
					[ResourceType.GEMS]: 400,
				},
				5: {
					[ResourceType.EVENTIUM]: 30_000,
					[ResourceType.GEMS]: 400,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.EASTER,
		},
	],
	[
		EventUpgradeIds.ea_legendary_easter_bunny,
		{
			levelUnlock: 1,
			heading: "upgrade.legendary_easter_bunny",
			description: "upgrade.easter_egg_cap_description",
			descriptionReplace: {
				"{number}": "5",
			},
			replaceValue: 5,
			price: {
				1: { [ResourceType.GEMS]: 1_000 },
				2: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 2,
			eventType: EventCategory.EASTER,
		},
	],
	[
		EventUpgradeIds.ea_egg_magnet,
		{
			levelUnlock: 1,
			heading: "upgrade.egg_magnet",
			description: "upgrade.easter_egg_distance_description",
			descriptionReplace: {
				"{number}": "100",
			},
			replaceValue: 100,
			price: {
				1: { [ResourceType.EVENTIUM]: 250_000 },
			},
			maxLevel: 1,
			eventType: EventCategory.EASTER,
		},
	],
	[
		EventUpgradeIds.ea_trading,
		{
			levelUnlock: 1,
			heading: "upgrade.trading",
			description: "event.easter_event.conversion_rate_description",
			descriptionReplace: {
				"{percent}": "2%",
			},
			replaceValue: 0.02,
			price: {
				1: { [ResourceType.EVENTIUM]: 1_000_000 },
				2: { [ResourceType.EVENTIUM]: 1_500_000 },
				3: { [ResourceType.EVENTIUM]: 2_000_000 },
				4: { [ResourceType.EVENTIUM]: 2_500_000 },
				5: { [ResourceType.EVENTIUM]: 3_000_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.EASTER,
		},
	],
	[
		EventUpgradeIds.ea_foreman,
		{
			levelUnlock: 1,
			heading: "upgrade.foreman",
			description: "upgrade.easter_egg_offline_income_description",
			descriptionReplace: {
				"{percent}": "100%",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 1,
			eventType: EventCategory.EASTER,
		},
	],
	[
		EventUpgradeIds.ea_eventium_income,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 25,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.EASTER,
			attributes: {
				income: 25,
				icon: "icon-eventium-income",
			},
		},
	],
	[
		EventUpgradeIds.ea_eventium_income2,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 5,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.EASTER,
			attributes: {
				income: 5,
				icon: "icon-eventium-income",
			},
		},
	],
	[
		EventUpgradeIds.ea_eventium_income3,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 15,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.EASTER,
			attributes: {
				income: 15,
				icon: "icon-eventium-income",
			},
		},
	],
	[
		EventUpgradeIds.ea_eventium_income4,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 30,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.EASTER,
			attributes: {
				income: 30,
				icon: "icon-eventium-income",
			},
		},
	],
	[
		EventUpgradeIds.ea_eventium_income5,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 60,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.EASTER,
			attributes: {
				income: 60,
				icon: "icon-eventium-income",
			},
		},
	],
	[
		EventUpgradeIds.ea_eastertide_special1,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 10,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.EASTER,
			attributes: {
				value: 10,
				icon: "icon-extra-eventium-painted-egg-pickups",
			},
		},
	],
	[
		EventUpgradeIds.ea_eastertide_special2,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 0.03,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.EASTER,
			attributes: {
				percentage: 3,
				icon: "icon-extra-chance-spawn-golden-egg-pickup",
			},
		},
	],
	[
		EventUpgradeIds.ea_eastertide_special4,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 100,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.EASTER,
			attributes: {
				value: 100,
				icon: "icon-extra-eventium-golden-egg-pickups",
			},
		},
	],
	// -------- FISHING UPGRADES START HERE --------
	[
		EventUpgradeIds.fi_trained_fishermen,
		{
			levelUnlock: 1,
			heading: "upgrade.trained_fishermen",
			description: "event.fishing_event.eventium_production_boost",
			descriptionReplace: {
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.EVENTIUM]: 1_000 },
				2: { [ResourceType.EVENTIUM]: 5_000 },
				3: { [ResourceType.EVENTIUM]: 20_000 },
				4: { [ResourceType.EVENTIUM]: 40_000 },
				5: { [ResourceType.EVENTIUM]: 75_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.FISHING,
		},
	],
	[
		EventUpgradeIds.fi_experienced_fishermen,
		{
			levelUnlock: 1,
			heading: "upgrade.experienced_fishermen",
			description: "event.fishing_event.eventium_production_boost",
			descriptionReplace: {
				"{percent}": "20%",
			},
			replaceValue: 0.2,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 100_000,
					[ResourceType.GEMS]: 100,
				},
				2: {
					[ResourceType.EVENTIUM]: 200_000,
					[ResourceType.GEMS]: 100,
				},
				3: {
					[ResourceType.EVENTIUM]: 350_000,
					[ResourceType.GEMS]: 100,
				},
				4: {
					[ResourceType.EVENTIUM]: 500_000,
					[ResourceType.GEMS]: 100,
				},
				5: {
					[ResourceType.EVENTIUM]: 750_000,
					[ResourceType.GEMS]: 100,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.FISHING,
		},
	],
	[
		EventUpgradeIds.fi_professional_fishermen,
		{
			levelUnlock: 1,
			heading: "upgrade.professional_fishermen",
			description: "event.fishing_event.eventium_production_boost",
			descriptionReplace: {
				"{percent}": "50%",
			},
			replaceValue: 0.5,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 2_500_000,
					[ResourceType.GEMS]: 250,
				},
				2: {
					[ResourceType.EVENTIUM]: 5_000_000,
					[ResourceType.GEMS]: 250,
				},
			},
			maxLevel: 2,
			eventType: EventCategory.FISHING,
		},
	],
	[
		EventUpgradeIds.fi_elite_fishermen,
		{
			levelUnlock: 1,
			heading: "upgrade.elite_fishermen",
			description: "event.fishing_event.eventium_production_boost",
			descriptionReplace: {
				"{percent}": "100%",
			},
			replaceValue: 1,
			price: {
				1: {
					[ResourceType.GEMS]: 1_000,
				},
			},
			maxLevel: 1,
			eventType: EventCategory.FISHING,
		},
	],
	[
		EventUpgradeIds.fi_sail,
		{
			levelUnlock: 1,
			heading: "upgrade.sail",
			description: "upgrade.raft_range_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 5_000_000,
					[ResourceType.GEMS]: 500,
				},
			},
			maxLevel: 1,
			eventType: EventCategory.FISHING,
		},
	],
	[
		EventUpgradeIds.fi_durable_materials,
		{
			levelUnlock: 1,
			heading: "upgrade.durable_materials",
			description: "upgrade.bottle_trap_range_description",
			replaceValue: 1,
			descriptionReplace: {
				"{number}": "1",
			},
			price: {
				1: {
					[ResourceType.EVENTIUM]: 7_500_000,
					[ResourceType.GEMS]: 750,
				},
				2: {
					[ResourceType.EVENTIUM]: 7_500_000,
					[ResourceType.GEMS]: 750,
				},
			},
			maxLevel: 2,
			eventType: EventCategory.FISHING,
		},
	],
	[
		EventUpgradeIds.fi_fish_feeding,
		{
			levelUnlock: 1,
			heading: "upgrade.fish_feeding",
			description: "upgrade.fish_feeding_description",
			descriptionReplace: {
				"{percent}": "25%",
			},
			replaceValue: 0.25,
			price: {
				1: { [ResourceType.EVENTIUM]: 1_500_000 },
			},
			maxLevel: 1,
			eventType: EventCategory.FISHING,
		},
	],
	[
		EventUpgradeIds.fi_fish_market,
		{
			levelUnlock: 1,
			heading: "upgrade.fish_market",
			description: "upgrade.fish_market_description",
			descriptionReplace: {
				"{percent}": "100%",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 1,
			eventType: EventCategory.FISHING,
		},
	],
	[
		EventUpgradeIds.fi_trading,
		{
			levelUnlock: 1,
			heading: "upgrade.trading",
			description: "event.fishing_event.conversion_rate_description",
			descriptionReplace: {
				"{percent}": "2%",
			},
			replaceValue: 0.02,
			price: {
				1: { [ResourceType.EVENTIUM]: 1_000_000 },
				2: { [ResourceType.EVENTIUM]: 2_000_000 },
				3: { [ResourceType.EVENTIUM]: 3_000_000 },
				4: { [ResourceType.EVENTIUM]: 4_000_000 },
				5: { [ResourceType.EVENTIUM]: 5_000_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.FISHING,
		},
	],
	[
		EventUpgradeIds.fi_foreman,
		{
			levelUnlock: 1,
			heading: "upgrade.foreman",
			description: "upgrade.tiny_fish_offline_income_description",
			descriptionReplace: {
				"{percent}": "100%",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 1,
			eventType: EventCategory.FISHING,
		},
	],
	[
		EventUpgradeIds.fi_eventium_income,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 0.2,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.FISHING,
			attributes: {
				percentage: 20,
				icon: "icon-upgrade-2201",
			},
		},
	],
	[
		EventUpgradeIds.fi_eventium_income2,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 0.8,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.FISHING,
			attributes: {
				percentage: 80,
				icon: "icon-upgrade-2203",
			},
		},
	],
	// -------- FIRE FESTIVAL UPGRADES START HERE --------
	[
		EventUpgradeIds.ff_maple_wood,
		{
			levelUnlock: 1,
			heading: "upgrade.maple_wood",
			description: "upgrade.building_production_flat_bonus_description",
			replaceValue: 1,
			descriptionReplace: {
				"{building}": `${FrameName.FIRE_FESTIVAL_PYRE}.name`,
				"{number}": "1",
			},
			price: {
				1: {
					[ResourceType.EVENTIUM]: 10,
				},
				2: {
					[ResourceType.EVENTIUM]: 30,
				},
				3: {
					[ResourceType.EVENTIUM]: 50,
				},
				4: {
					[ResourceType.EVENTIUM]: 100,
				},
				5: {
					[ResourceType.EVENTIUM]: 150,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.FIREFEST,
		},
	],
	[
		EventUpgradeIds.ff_beech_wood,
		{
			levelUnlock: 1,
			heading: "upgrade.beech_wood",
			description: "upgrade.building_production_flat_bonus_description",
			replaceValue: 2,
			descriptionReplace: {
				"{building}": `${FrameName.FIRE_FESTIVAL_PYRE}.name`,
				"{number}": "2",
			},
			price: {
				1: {
					[ResourceType.EVENTIUM]: 500,
					[ResourceType.GEMS]: 200,
				},
				2: {
					[ResourceType.EVENTIUM]: 1_500,
					[ResourceType.GEMS]: 200,
				},
				3: {
					[ResourceType.EVENTIUM]: 3_000,
					[ResourceType.GEMS]: 200,
				},
				4: {
					[ResourceType.EVENTIUM]: 5_000,
					[ResourceType.GEMS]: 200,
				},
				5: {
					[ResourceType.EVENTIUM]: 7_500,
					[ResourceType.GEMS]: 200,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.FIREFEST,
		},
	],
	[
		EventUpgradeIds.ff_black_locust_wood,
		{
			levelUnlock: 1,
			heading: "upgrade.black_locust_wood",
			description: "upgrade.building_production_flat_bonus_description",
			replaceValue: 3,
			descriptionReplace: {
				"{building}": `${FrameName.FIRE_FESTIVAL_PYRE}.name`,
				"{number}": "3",
			},
			price: {
				1: {
					[ResourceType.EVENTIUM]: 50_000,
					[ResourceType.GEMS]: 400,
				},
				2: {
					[ResourceType.EVENTIUM]: 65_000,
					[ResourceType.GEMS]: 400,
				},
				3: {
					[ResourceType.EVENTIUM]: 80_000,
					[ResourceType.GEMS]: 400,
				},
				4: {
					[ResourceType.EVENTIUM]: 160_000,
					[ResourceType.GEMS]: 400,
				},
				5: {
					[ResourceType.EVENTIUM]: 320_000,
					[ResourceType.GEMS]: 400,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.FIREFEST,
		},
	],
	[
		EventUpgradeIds.ff_oak_wood,
		{
			levelUnlock: 1,
			heading: "upgrade.oak_wood",
			description: "upgrade.building_production_flat_bonus_description",
			replaceValue: 10,
			descriptionReplace: {
				"{building}": `${FrameName.FIRE_FESTIVAL_PYRE}.name`,
				"{number}": "10",
			},
			price: {
				1: {
					[ResourceType.GEMS]: 2_000,
				},
				2: {
					[ResourceType.GEMS]: 2_000,
				},
			},
			maxLevel: 2,
			eventType: EventCategory.FIREFEST,
		},
	],
	[
		EventUpgradeIds.ff_cleft_wood,
		{
			levelUnlock: 1,
			heading: "upgrade.cleft_wood",
			description:
				"upgrade.building_production_town_level_bonus_description",
			descriptionReplace: {
				"{building}": `${FrameName.FIRE_FESTIVAL_PYRE}.name`,
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 1_000,
					[ResourceType.GEMS]: 200,
				},
				2: {
					[ResourceType.EVENTIUM]: 2_500,
					[ResourceType.GEMS]: 200,
				},
				3: {
					[ResourceType.EVENTIUM]: 4_000,
					[ResourceType.GEMS]: 200,
				},
				4: {
					[ResourceType.EVENTIUM]: 8_000,
					[ResourceType.GEMS]: 200,
				},
				5: {
					[ResourceType.EVENTIUM]: 16_000,
					[ResourceType.GEMS]: 200,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.FIREFEST,
		},
	],
	[
		EventUpgradeIds.ff_seasoned_wood,
		{
			levelUnlock: 1,
			heading: "upgrade.seasoned_wood",
			description:
				"upgrade.building_production_town_level_bonus_description",
			descriptionReplace: {
				"{building}": `${FrameName.FIRE_FESTIVAL_PYRE}.name`,
				"{number}": "2",
			},
			replaceValue: 2,
			price: {
				1: {
					[ResourceType.GEMS]: 1_500,
				},
				2: {
					[ResourceType.GEMS]: 1_500,
				},
			},
			maxLevel: 2,
			eventType: EventCategory.FIREFEST,
		},
	],
	[
		EventUpgradeIds.ff_apple_wood,
		{
			levelUnlock: 1,
			heading: "upgrade.apple_wood",
			description: "event.firefest_event.eventium_production_boost",
			descriptionReplace: {
				"{percent}": "2%",
			},
			replaceValue: 0.02,
			price: {
				1: { [ResourceType.EVENTIUM]: 15 },
				2: { [ResourceType.EVENTIUM]: 30 },
				3: { [ResourceType.EVENTIUM]: 100 },
				4: { [ResourceType.EVENTIUM]: 200 },
				5: { [ResourceType.EVENTIUM]: 300 },
			},
			maxLevel: 5,
			eventType: EventCategory.FIREFEST,
		},
	],
	[
		EventUpgradeIds.ff_cherry_wood,
		{
			levelUnlock: 1,
			heading: "upgrade.cherry_wood",
			description: "event.firefest_event.eventium_production_boost",
			descriptionReplace: {
				"{percent}": "3%",
			},
			replaceValue: 0.03,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 500,
					[ResourceType.GEMS]: 200,
				},
				2: {
					[ResourceType.EVENTIUM]: 650,
					[ResourceType.GEMS]: 200,
				},
				3: {
					[ResourceType.EVENTIUM]: 800,
					[ResourceType.GEMS]: 200,
				},
				4: {
					[ResourceType.EVENTIUM]: 1_600,
					[ResourceType.GEMS]: 200,
				},
				5: {
					[ResourceType.EVENTIUM]: 3_200,
					[ResourceType.GEMS]: 200,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.FIREFEST,
		},
	],
	[
		EventUpgradeIds.ff_birch_wood,
		{
			levelUnlock: 1,
			heading: "upgrade.birch_wood",
			description: "event.firefest_event.eventium_production_boost",
			descriptionReplace: {
				"{percent}": "5%",
			},
			replaceValue: 0.05,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 75_000,
					[ResourceType.GEMS]: 400,
				},
				2: {
					[ResourceType.EVENTIUM]: 90_000,
					[ResourceType.GEMS]: 400,
				},
				3: {
					[ResourceType.EVENTIUM]: 135_000,
					[ResourceType.GEMS]: 400,
				},
				4: {
					[ResourceType.EVENTIUM]: 270_000,
					[ResourceType.GEMS]: 400,
				},
				5: {
					[ResourceType.EVENTIUM]: 540_000,
					[ResourceType.GEMS]: 400,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.FIREFEST,
		},
	],
	[
		EventUpgradeIds.ff_hickory_wood,
		{
			levelUnlock: 1,
			heading: "upgrade.hickory_wood",
			description: "event.firefest_event.eventium_production_boost",
			descriptionReplace: {
				"{percent}": "25%",
			},
			replaceValue: 0.25,
			price: {
				1: {
					[ResourceType.GEMS]: 1_500,
				},
				2: {
					[ResourceType.GEMS]: 1_500,
				},
			},
			maxLevel: 2,
			eventType: EventCategory.FIREFEST,
		},
	],
	[
		EventUpgradeIds.ff_town_celebrations,
		{
			levelUnlock: 1,
			heading: "upgrade.town_celebrations",
			description: "upgrade.town_upgrade_ember_reward_description",
			descriptionReplace: {
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 100_000,
				},
			},
			maxLevel: 1,
			eventType: EventCategory.FIREFEST,
		},
	],
	[
		EventUpgradeIds.ff_carbon_tax,
		{
			levelUnlock: 1,
			heading: "upgrade.carbon_tax",
			description: "upgrade.tax_collecting_embers_description",
			descriptionReplace: {
				"{number}": "10",
			},
			replaceValue: 10,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 2_000_000,
					[ResourceType.GEMS]: 1_200,
				},
			},
			maxLevel: 1,
			eventType: EventCategory.FIREFEST,
		},
	],
	[
		EventUpgradeIds.ff_kindling_of_passion,
		{
			levelUnlock: 1,
			heading: "upgrade.kindling_of_passion",
			description: "upgrade.increase_building_limit_description",
			replaceValue: 1,
			descriptionReplace: {
				"{building}": `${FrameName.FIRE_FESTIVAL_PYRE}.name`,
				"{number}": "1",
			},
			price: {
				1: {
					[ResourceType.EVENTIUM]: 120_000,
					[ResourceType.GEMS]: 500,
				},
				2: {
					[ResourceType.EVENTIUM]: 150_000,
					[ResourceType.GEMS]: 600,
				},
				3: {
					[ResourceType.EVENTIUM]: 200_000,
					[ResourceType.GEMS]: 700,
				},
				4: {
					[ResourceType.EVENTIUM]: 300_000,
					[ResourceType.GEMS]: 800,
				},
				5: {
					[ResourceType.EVENTIUM]: 400_000,
					[ResourceType.GEMS]: 1_000,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.FIREFEST,
		},
	],
	[
		EventUpgradeIds.ff_kindling_of_hope,
		{
			levelUnlock: 1,
			heading: "upgrade.kindling_of_hope",
			description: "upgrade.increase_building_limit_description",
			replaceValue: 1,
			descriptionReplace: {
				"{building}": `${FrameName.FIRE_FESTIVAL_PYRE}.name`,
				"{number}": "1",
			},
			price: {
				1: {
					[ResourceType.EVENTIUM]: 500_000,
					[ResourceType.GEMS]: 500,
				},
				2: {
					[ResourceType.EVENTIUM]: 700_000,
					[ResourceType.GEMS]: 600,
				},
				3: {
					[ResourceType.EVENTIUM]: 1_000_000,
					[ResourceType.GEMS]: 700,
				},
				4: {
					[ResourceType.EVENTIUM]: 2_000_000,
					[ResourceType.GEMS]: 800,
				},
				5: {
					[ResourceType.EVENTIUM]: 4_000_000,
					[ResourceType.GEMS]: 1_000,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.FIREFEST,
		},
	],
	[
		EventUpgradeIds.ff_trading,
		{
			levelUnlock: 1,
			heading: "upgrade.trading",
			description: "event.firefest_event.conversion_rate_description",
			descriptionReplace: {
				"{percent}": "2%",
			},
			replaceValue: 0.02,
			price: {
				1: { [ResourceType.EVENTIUM]: 1_000_000 },
				2: { [ResourceType.EVENTIUM]: 1_500_000 },
				3: { [ResourceType.EVENTIUM]: 2_000_000 },
				4: { [ResourceType.EVENTIUM]: 2_500_000 },
				5: { [ResourceType.EVENTIUM]: 3_000_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.FIREFEST,
		},
	],
	[
		EventUpgradeIds.ff_foreman,
		{
			levelUnlock: 1,
			heading: "upgrade.foreman",
			description: "upgrade.festive_ember_offline_income_description",
			descriptionReplace: {
				"{percent}": "100%",
			},
			replaceValue: 1,
			price: {
				1: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 1,
			eventType: EventCategory.FIREFEST,
		},
	],
	[
		EventUpgradeIds.ff_special_01,
		{
			levelUnlock: 1,
			heading: "",
			description:
				"upgrade.building_production_town_level_bonus_description",
			replaceValue: 1,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.FIREFEST,
			attributes: {
				value: 1,
				building: FrameName.FIRE_FESTIVAL_PYRE,
				icon: "icon-firefest-bonus-bonfire-prod-plus1",
			},
		},
	],
	[
		EventUpgradeIds.ff_special_02,
		{
			levelUnlock: 1,
			heading: "",
			description: "upgrade.tax_collecting_embers_description",
			replaceValue: 10,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.FIREFEST,
			attributes: {
				value: 10,
				icon: "icon-firefest-bonus-tax-collection-plus10-coals",
			},
		},
	],
	[
		EventUpgradeIds.ff_special_03,
		{
			levelUnlock: 1,
			heading: "",
			description:
				"upgrade.building_production_town_level_bonus_description",
			replaceValue: 2,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.FIREFEST,
			attributes: {
				value: 2,
				building: FrameName.FIRE_FESTIVAL_PYRE,
				icon: "icon-firefest-bonus-bonfire-prod-plus2",
			},
		},
	],
	[
		EventUpgradeIds.ff_special_04,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 0.5,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.FIREFEST,
			attributes: {
				percentage: 50,
				icon: "icon-firefest-income-bonus-50pct",
			},
		},
	],
	[
		EventUpgradeIds.ff_bundle_1,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 0.05,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.FIREFEST,
			attributes: {
				percentage: 5,
				icon: "icon-firefest-income-bonus-5-10pct",
			},
		},
	],
	[
		EventUpgradeIds.ff_bundle_2,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 0.1,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.FIREFEST,
			attributes: {
				percentage: 10,
				icon: "icon-firefest-income-bonus-5-10pct",
			},
		},
	],
	[
		EventUpgradeIds.ff_bundle_3,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 0.25,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.FIREFEST,
			attributes: {
				percentage: 25,
				icon: "icon-firefest-income-bonus-25pct",
			},
		},
	],
	[
		EventUpgradeIds.ff_bundle_4,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 0.5,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.FIREFEST,
			attributes: {
				percentage: 50,
				icon: "icon-firefest-income-bonus-50pct",
			},
		},
	],
	// -------- SOLAR FAIR UPGRADES START HERE --------
	[
		EventUpgradeIds.sf_good_harvest,
		{
			levelUnlock: 1,
			heading: "upgrade.good_harvest",
			description: "upgrade.building_production_flat_bonus_description",
			replaceValue: 2,
			descriptionReplace: {
				"{building}": `${FrameName.BOUNTIFUL_MARKETPLACE}.name`,
				"{number}": "2",
			},
			price: {
				1: {
					[ResourceType.EVENTIUM]: 2_500,
				},
				2: {
					[ResourceType.EVENTIUM]: 5_000,
				},
				3: {
					[ResourceType.EVENTIUM]: 10_000,
				},
				4: {
					[ResourceType.EVENTIUM]: 20_000,
				},
				5: {
					[ResourceType.EVENTIUM]: 30_000,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.SOLAR,
		},
	],
	[
		EventUpgradeIds.sf_great_harvest,
		{
			levelUnlock: 1,
			heading: "upgrade.great_harvest",
			description: "upgrade.building_production_flat_bonus_description",
			replaceValue: 4,
			descriptionReplace: {
				"{building}": `${FrameName.BOUNTIFUL_MARKETPLACE}.name`,
				"{number}": "4",
			},
			price: {
				1: {
					[ResourceType.EVENTIUM]: 60_000,
				},
				2: {
					[ResourceType.EVENTIUM]: 100_000,
				},
				3: {
					[ResourceType.EVENTIUM]: 150_000,
				},
				4: {
					[ResourceType.EVENTIUM]: 200_000,
				},
				5: {
					[ResourceType.EVENTIUM]: 250_000,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.SOLAR,
		},
	],
	[
		EventUpgradeIds.sf_bountiful_harvest,
		{
			levelUnlock: 1,
			heading: "upgrade.bountiful_harvest",
			description: "upgrade.building_production_flat_bonus_description",
			replaceValue: 8,
			descriptionReplace: {
				"{building}": `${FrameName.BOUNTIFUL_MARKETPLACE}.name`,
				"{number}": "8",
			},
			price: {
				1: {
					[ResourceType.EVENTIUM]: 300_000,
					[ResourceType.GEMS]: 300,
				},
				2: {
					[ResourceType.EVENTIUM]: 600_000,
					[ResourceType.GEMS]: 600,
				},
				3: {
					[ResourceType.EVENTIUM]: 900_000,
					[ResourceType.GEMS]: 900,
				},
				4: {
					[ResourceType.EVENTIUM]: 1_200_000,
					[ResourceType.GEMS]: 1_200,
				},
				5: {
					[ResourceType.EVENTIUM]: 1_500_000,
					[ResourceType.GEMS]: 1_500,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.SOLAR,
		},
	],
	[
		EventUpgradeIds.sf_historic_harvest,
		{
			levelUnlock: 1,
			heading: "upgrade.historic_harvest",
			description: "upgrade.building_production_flat_bonus_description",
			replaceValue: 15,
			descriptionReplace: {
				"{building}": `${FrameName.BOUNTIFUL_MARKETPLACE}.name`,
				"{number}": "15",
			},
			price: {
				1: {
					[ResourceType.GEMS]: 1_500,
				},
				2: {
					[ResourceType.GEMS]: 1_500,
				},
				3: {
					[ResourceType.GEMS]: 1_500,
				},
			},
			maxLevel: 3,
			eventType: EventCategory.SOLAR,
		},
	],
	[
		EventUpgradeIds.sf_skilled_gatherers,
		{
			levelUnlock: 1,
			heading: "upgrade.skilled_gatherers",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.BOUNTIFUL_MARKETPLACE}.name`,
				"{percent}": "2%",
			},
			replaceValue: 0.02,
			price: {
				1: { [ResourceType.EVENTIUM]: 4_000 },
				2: { [ResourceType.EVENTIUM]: 8_000 },
				3: { [ResourceType.EVENTIUM]: 15_000 },
				4: { [ResourceType.EVENTIUM]: 25_000 },
				5: { [ResourceType.EVENTIUM]: 50_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.SOLAR,
		},
	],
	[
		EventUpgradeIds.sf_adept_hunters,
		{
			levelUnlock: 1,
			heading: "upgrade.adept_hunters",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.BOUNTIFUL_MARKETPLACE}.name`,
				"{percent}": "4%",
			},
			replaceValue: 0.04,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 80_000,
				},
				2: {
					[ResourceType.EVENTIUM]: 120_000,
				},
				3: {
					[ResourceType.EVENTIUM]: 180_000,
				},
				4: {
					[ResourceType.EVENTIUM]: 240_000,
				},
				5: {
					[ResourceType.EVENTIUM]: 300_000,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.SOLAR,
		},
	],
	[
		EventUpgradeIds.sf_experienced_farmers,
		{
			levelUnlock: 1,
			heading: "upgrade.experienced_farmers",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.BOUNTIFUL_MARKETPLACE}.name`,
				"{percent}": "8%",
			},
			replaceValue: 0.08,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 400_000,
					[ResourceType.GEMS]: 400,
				},
				2: {
					[ResourceType.EVENTIUM]: 800_000,
					[ResourceType.GEMS]: 800,
				},
				3: {
					[ResourceType.EVENTIUM]: 1_200_000,
					[ResourceType.GEMS]: 1_200,
				},
				4: {
					[ResourceType.EVENTIUM]: 1_600_000,
					[ResourceType.GEMS]: 1_600,
				},
				5: {
					[ResourceType.EVENTIUM]: 2_000_000,
					[ResourceType.GEMS]: 2_000,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.SOLAR,
		},
	],
	[
		EventUpgradeIds.sf_master_bakers,
		{
			levelUnlock: 1,
			heading: "upgrade.master_bakers",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.BOUNTIFUL_MARKETPLACE}.name`,
				"{percent}": "15%",
			},
			replaceValue: 0.15,
			price: {
				1: { [ResourceType.GEMS]: 2_000 },
				2: { [ResourceType.GEMS]: 2_000 },
				3: { [ResourceType.GEMS]: 2_000 },
			},
			maxLevel: 3,
			eventType: EventCategory.SOLAR,
		},
	],
	[
		EventUpgradeIds.sf_free_market_economy,
		{
			levelUnlock: 1,
			heading: "upgrade.free_market_economy",
			description: "upgrade.increase_building_limit_description",
			replaceValue: 1,
			descriptionReplace: {
				"{building}": `${FrameName.BOUNTIFUL_MARKETPLACE}.name`,
				"{number}": "1",
			},
			price: {
				1: {
					[ResourceType.EVENTIUM]: 40_000,
				},
				2: {
					[ResourceType.EVENTIUM]: 200_000,
				},
				3: {
					[ResourceType.EVENTIUM]: 800_000,
				},
				4: {
					[ResourceType.EVENTIUM]: 2_400_000,
				},
				5: {
					[ResourceType.EVENTIUM]: 4_800_000,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.SOLAR,
		},
	],
	[
		EventUpgradeIds.sf_booming_economy,
		{
			levelUnlock: 1,
			heading: "upgrade.booming_economy",
			description: "upgrade.increase_building_limit_description",
			replaceValue: 1,
			descriptionReplace: {
				"{building}": `${FrameName.BOUNTIFUL_MARKETPLACE}.name`,
				"{number}": "1",
			},
			price: {
				1: {
					[ResourceType.EVENTIUM]: 5_000_000,
					[ResourceType.GEMS]: 500,
				},
				2: {
					[ResourceType.EVENTIUM]: 10_000_000,
					[ResourceType.GEMS]: 600,
				},
				3: {
					[ResourceType.EVENTIUM]: 15_000_000,
					[ResourceType.GEMS]: 700,
				},
				4: {
					[ResourceType.EVENTIUM]: 20_000_000,
					[ResourceType.GEMS]: 800,
				},
				5: {
					[ResourceType.EVENTIUM]: 25_000_000,
					[ResourceType.GEMS]: 1_000,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.SOLAR,
		},
	],
	[
		EventUpgradeIds.sf_peddler,
		{
			levelUnlock: 1,
			heading: "upgrade.peddler",
			description: "upgrade.sun_doubloon_offline_income_description",
			descriptionReplace: {
				"{percent}": "100%",
			},
			replaceValue: 1,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 100_000_000,
					[ResourceType.GEMS]: 1_000,
				},
			},
			maxLevel: 1,
			eventType: EventCategory.SOLAR,
		},
	],
	[
		EventUpgradeIds.sf_special_01,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 10,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.SOLAR,
			attributes: {
				value: 10,
				building: FrameName.BOUNTIFUL_MARKETPLACE,
				icon: "icon-solarfair-bonus-bountiful-prod-plus10",
			},
		},
	],
	[
		EventUpgradeIds.sf_special_02,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 2,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.SOLAR,
			attributes: {
				value: 2,
				building: FrameName.BOUNTIFUL_MARKETPLACE,
				icon: "icon-solarfair-bonus-bountiful-limit-plus2",
			},
		},
	],
	[
		EventUpgradeIds.sf_special_03,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 30,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.SOLAR,
			attributes: {
				value: 30,
				building: FrameName.BOUNTIFUL_MARKETPLACE,
				icon: "icon-solarfair-bonus-bountiful-prod-plus40",
			},
		},
	],
	[
		EventUpgradeIds.sf_bundle_1,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 0.05,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.SOLAR,
			attributes: {
				percentage: 5,
				icon: "icon-solarfair-income-bonus-5pct",
			},
		},
	],
	[
		EventUpgradeIds.sf_bundle_2,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 0.1,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.SOLAR,
			attributes: {
				percentage: 10,
				icon: "icon-solarfair-income-bonus-10pct",
			},
		},
	],
	[
		EventUpgradeIds.sf_bundle_3,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 0.15,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.SOLAR,
			attributes: {
				percentage: 15,
				icon: "icon-solarfair-income-bonus-20pct",
			},
		},
	],
	// -------- HUNTING UPGRADES START HERE --------
	[
		EventUpgradeIds.hu_duck_season,
		{
			levelUnlock: 1,
			heading: "upgrade.duck_season",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.HUNTING_CAMP}.name`,
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.EVENTIUM]: 1_000 },
				2: { [ResourceType.EVENTIUM]: 5_000 },
				3: { [ResourceType.EVENTIUM]: 20_000 },
				4: { [ResourceType.EVENTIUM]: 40_000 },
				5: { [ResourceType.EVENTIUM]: 75_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.HUNTING,
		},
	],
	[
		EventUpgradeIds.hu_fox_season,
		{
			levelUnlock: 1,
			heading: "upgrade.fox_season",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.HIGHLAND_HUNTER}.name`,
				"{percent}": "10%",
			},
			replaceValue: 0.1,
			price: {
				1: { [ResourceType.EVENTIUM]: 1_000 },
				2: { [ResourceType.EVENTIUM]: 5_000 },
				3: { [ResourceType.EVENTIUM]: 20_000 },
				4: { [ResourceType.EVENTIUM]: 40_000 },
				5: { [ResourceType.EVENTIUM]: 75_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.HUNTING,
		},
	],
	[
		EventUpgradeIds.hu_rabbit_season,
		{
			levelUnlock: 1,
			heading: "upgrade.rabbit_season",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.HUNTING_CAMP}.name`,
				"{percent}": "20%",
			},
			replaceValue: 0.2,
			price: {
				1: { [ResourceType.EVENTIUM]: 100_000 },
				2: { [ResourceType.EVENTIUM]: 200_000 },
				3: { [ResourceType.EVENTIUM]: 350_000 },
				4: { [ResourceType.EVENTIUM]: 500_000 },
				5: { [ResourceType.EVENTIUM]: 750_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.HUNTING,
		},
	],
	[
		EventUpgradeIds.hu_deer_season,
		{
			levelUnlock: 1,
			heading: "upgrade.deer_season",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.HIGHLAND_HUNTER}.name`,
				"{percent}": "20%",
			},
			replaceValue: 0.2,
			price: {
				1: { [ResourceType.EVENTIUM]: 100_000 },
				2: { [ResourceType.EVENTIUM]: 200_000 },
				3: { [ResourceType.EVENTIUM]: 350_000 },
				4: { [ResourceType.EVENTIUM]: 500_000 },
				5: { [ResourceType.EVENTIUM]: 750_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.HUNTING,
		},
	],
	[
		EventUpgradeIds.hu_boar_season,
		{
			levelUnlock: 1,
			heading: "upgrade.boar_season",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.HUNTING_CAMP}.name`,
				"{percent}": "50%",
			},
			replaceValue: 0.5,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 2_500_000,
					[ResourceType.GEMS]: 250,
				},
				2: {
					[ResourceType.EVENTIUM]: 5_000_000,
					[ResourceType.GEMS]: 500,
				},
			},
			maxLevel: 2,
			eventType: EventCategory.HUNTING,
		},
	],
	[
		EventUpgradeIds.hu_wolf_season,
		{
			levelUnlock: 1,
			heading: "upgrade.wolf_season",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.HIGHLAND_HUNTER}.name`,
				"{percent}": "50%",
			},
			replaceValue: 0.5,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 2_500_000,
					[ResourceType.GEMS]: 250,
				},
				2: {
					[ResourceType.EVENTIUM]: 5_000_000,
					[ResourceType.GEMS]: 500,
				},
			},
			maxLevel: 2,
			eventType: EventCategory.HUNTING,
		},
	],
	[
		EventUpgradeIds.hu_red_deer_season,
		{
			levelUnlock: 1,
			heading: "upgrade.red_deer_season",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.HUNTING_CAMP}.name`,
				"{percent}": "100%",
			},
			replaceValue: 1,
			price: {
				1: {
					[ResourceType.GEMS]: 1_000,
				},
			},
			maxLevel: 1,
			eventType: EventCategory.HUNTING,
		},
	],
	[
		EventUpgradeIds.hu_bear_season,
		{
			levelUnlock: 1,
			heading: "upgrade.bear_season",
			description: "upgrade.building_production_description",
			descriptionReplace: {
				"{building}": `${FrameName.HIGHLAND_HUNTER}.name`,
				"{percent}": "100%",
			},
			replaceValue: 1,
			price: {
				1: {
					[ResourceType.GEMS]: 1_000,
				},
			},
			maxLevel: 1,
			eventType: EventCategory.HUNTING,
		},
	],
	[
		EventUpgradeIds.hu_tracking,
		{
			levelUnlock: 1,
			heading: "upgrade.tracking",
			description: "upgrade.effective_range_description",
			descriptionReplace: {
				"{building}": `${FrameName.HUNTING_CAMP}.name`,
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 5_000_000,
					[ResourceType.GEMS]: 500,
				},
				2: {
					[ResourceType.EVENTIUM]: 7_500_000,
					[ResourceType.GEMS]: 750,
				},
			},
			maxLevel: 2,
			eventType: EventCategory.HUNTING,
		},
	],
	[
		EventUpgradeIds.hu_falconry,
		{
			levelUnlock: 1,
			heading: "upgrade.falconry",
			description: "upgrade.effective_range_description",
			descriptionReplace: {
				"{building}": `${FrameName.HIGHLAND_HUNTER}.name`,
				"{number}": "1",
			},
			replaceValue: 1,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 5_000_000,
					[ResourceType.GEMS]: 500,
				},
				2: {
					[ResourceType.EVENTIUM]: 7_500_000,
					[ResourceType.GEMS]: 750,
				},
			},
			maxLevel: 2,
			eventType: EventCategory.HUNTING,
		},
	],
	[
		EventUpgradeIds.hu_mountaineering,
		{
			levelUnlock: 1,
			heading: "upgrade.mountaineering",
			description: "upgrade.mountaineering_description",
			descriptionReplace: {
				"{building}": `${FrameName.HIGHLAND_HUNTER}.name`,
			},
			replaceValue: 1, // enable capability
			price: {
				1: { [ResourceType.EVENTIUM]: 3_000_000 },
			},
			maxLevel: 1,
			eventType: EventCategory.HUNTING,
		},
	],
	[
		EventUpgradeIds.hu_trophy_exhibits,
		{
			levelUnlock: 1,
			heading: "upgrade.trophy_exhibits",
			description: "upgrade.trophy_exhibits_description",
			descriptionReplace: {
				"{percent}": "40%",
			},
			replaceValue: 0.4,
			price: {
				1: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 1,
			eventType: EventCategory.HUNTING,
		},
	],
	[
		EventUpgradeIds.hu_trading,
		{
			levelUnlock: 1,
			heading: "upgrade.trading",
			description: "event.conversion_rate_description",
			descriptionReplace: {
				"{percent}": "2%",
			},
			replaceValue: 0.02,
			price: {
				1: { [ResourceType.EVENTIUM]: 1_000_000 },
				2: { [ResourceType.EVENTIUM]: 2_000_000 },
				3: { [ResourceType.EVENTIUM]: 3_000_000 },
				4: { [ResourceType.EVENTIUM]: 4_000_000 },
				5: { [ResourceType.EVENTIUM]: 5_000_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.HUNTING,
		},
	],
	[
		EventUpgradeIds.hu_foreman,
		{
			levelUnlock: 1,
			heading: "upgrade.foreman",
			description: "upgrade.eventium_offline_income_description",
			descriptionReplace: {
				"{percent}": "100%",
			},
			replaceValue: 1,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 100_000_000,
					[ResourceType.GEMS]: 1_000,
				},
			},
			maxLevel: 1,
			eventType: EventCategory.HUNTING,
		},
	],
	[
		EventUpgradeIds.hu_bow_1,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 0.1,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.HUNTING,
			attributes: {
				percentage: 10,
				icon: "icon-hunting-income-bonus-10-15pct",
			},
		},
	],
	[
		EventUpgradeIds.hu_bow_3,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 0.4,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.HUNTING,
			attributes: {
				percentage: 40,
				icon: "icon-hunting-income-bonus-40pct",
			},
		},
	],
	[
		EventUpgradeIds.hu_horse_2,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 0.15,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.HUNTING,
			attributes: {
				percentage: 15,
				icon: "icon-hunting-income-bonus-10-15pct",
			},
		},
	],
	[
		EventUpgradeIds.hu_horse_4,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 0.6,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.HUNTING,
			attributes: {
				percentage: 60,
				icon: "icon-hunting-income-bonus-60pct",
			},
		},
	],
	// -------- HALLOWEEN UPGRADES START HERE --------
	[
		EventUpgradeIds.ha_pumpkin_carving,
		{
			levelUnlock: 1,
			heading: "upgrade.pumpkin_carving",
			description: "event.eventium_production_boost_flat",
			descriptionReplace: {
				"{number}": "4",
			},
			replaceValue: 4,
			price: {
				1: { [ResourceType.EVENTIUM]: 100 },
				2: { [ResourceType.EVENTIUM]: 200 },
				3: { [ResourceType.EVENTIUM]: 400 },
				4: { [ResourceType.EVENTIUM]: 800 },
				5: { [ResourceType.EVENTIUM]: 1_500 },
			},
			maxLevel: 5,
			eventType: EventCategory.HALLOWEEN,
		},
	],
	[
		EventUpgradeIds.ha_trained_carvers,
		{
			levelUnlock: 1,
			heading: "upgrade.trained_carvers",
			description: "event.eventium_production_boost_flat",
			descriptionReplace: {
				"{number}": "8",
			},
			replaceValue: 8,
			price: {
				1: { [ResourceType.EVENTIUM]: 5_000 },
				2: { [ResourceType.EVENTIUM]: 10_000 },
				3: { [ResourceType.EVENTIUM]: 20_000 },
				4: { [ResourceType.EVENTIUM]: 40_000 },
				5: { [ResourceType.EVENTIUM]: 80_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.HALLOWEEN,
		},
	],
	[
		EventUpgradeIds.ha_artistic_carvers,
		{
			levelUnlock: 1,
			heading: "upgrade.artistic_carvers",
			description: "event.eventium_production_boost_flat",
			descriptionReplace: {
				"{number}": "16",
			},
			replaceValue: 16,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 150_000,
					[ResourceType.GEMS]: 600,
				},
				2: {
					[ResourceType.EVENTIUM]: 300_000,
					[ResourceType.GEMS]: 600,
				},
				3: {
					[ResourceType.EVENTIUM]: 450_000,
					[ResourceType.GEMS]: 600,
				},
				4: {
					[ResourceType.EVENTIUM]: 600_000,
					[ResourceType.GEMS]: 600,
				},
				5: {
					[ResourceType.EVENTIUM]: 750_000,
					[ResourceType.GEMS]: 600,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.HALLOWEEN,
		},
	],
	[
		EventUpgradeIds.ha_halloween_workshops,
		{
			levelUnlock: 1,
			heading: "upgrade.halloween_workshops",
			description: "event.eventium_production_boost_flat",
			descriptionReplace: {
				"{number}": "30",
			},
			replaceValue: 30,
			price: {
				1: { [ResourceType.GEMS]: 1_000 },
				2: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 2,
			eventType: EventCategory.HALLOWEEN,
		},
	],
	[
		EventUpgradeIds.ha_small_pumpkin,
		{
			levelUnlock: 1,
			heading: "upgrade.small_pumpkin",
			description: "upgrade.pumpkin_reward_description",
			descriptionReplace: {
				"{number}": "4",
			},
			replaceValue: 4,
			price: {
				1: { [ResourceType.EVENTIUM]: 150 },
				2: { [ResourceType.EVENTIUM]: 300 },
				3: { [ResourceType.EVENTIUM]: 600 },
				4: { [ResourceType.EVENTIUM]: 1_000 },
				5: { [ResourceType.EVENTIUM]: 2_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.HALLOWEEN,
		},
	],
	[
		EventUpgradeIds.ha_medium_pumpkin,
		{
			levelUnlock: 1,
			heading: "upgrade.medium_pumpkin",
			description: "upgrade.pumpkin_reward_description",
			descriptionReplace: {
				"{number}": "8",
			},
			replaceValue: 8,
			price: {
				1: { [ResourceType.EVENTIUM]: 4_000 },
				2: { [ResourceType.EVENTIUM]: 8_000 },
				3: { [ResourceType.EVENTIUM]: 15_000 },
				4: { [ResourceType.EVENTIUM]: 25_000 },
				5: { [ResourceType.EVENTIUM]: 50_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.HALLOWEEN,
		},
	],
	[
		EventUpgradeIds.ha_large_pumpkin,
		{
			levelUnlock: 1,
			heading: "upgrade.large_pumpkin",
			description: "upgrade.pumpkin_reward_description",
			descriptionReplace: {
				"{number}": "16",
			},
			replaceValue: 16,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 75_000,
					[ResourceType.GEMS]: 400,
				},
				2: {
					[ResourceType.EVENTIUM]: 100_000,
					[ResourceType.GEMS]: 400,
				},
				3: {
					[ResourceType.EVENTIUM]: 150_000,
					[ResourceType.GEMS]: 400,
				},
				4: {
					[ResourceType.EVENTIUM]: 200_000,
					[ResourceType.GEMS]: 400,
				},
				5: {
					[ResourceType.EVENTIUM]: 300_000,
					[ResourceType.GEMS]: 400,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.HALLOWEEN,
		},
	],
	[
		EventUpgradeIds.ha_giant_pumpkin,
		{
			levelUnlock: 1,
			heading: "upgrade.giant_pumpkin",
			description: "upgrade.pumpkin_reward_description",
			descriptionReplace: {
				"{number}": "30",
			},
			replaceValue: 30,
			price: {
				1: { [ResourceType.GEMS]: 600 },
				2: { [ResourceType.GEMS]: 600 },
				3: { [ResourceType.GEMS]: 600 },
			},
			maxLevel: 3,
			eventType: EventCategory.HALLOWEEN,
		},
	],
	[
		EventUpgradeIds.ha_will_o_the_wisp,
		{
			levelUnlock: 1,
			heading: "upgrade.will_o_the_wisp",
			description: "upgrade.jack_o_lantern_drop_rate_description",
			descriptionReplace: {
				"{percent}": "1%",
			},
			replaceValue: 0.01,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 15_000,
					[ResourceType.GEMS]: 400,
				},
				2: {
					[ResourceType.EVENTIUM]: 30_000,
					[ResourceType.GEMS]: 400,
				},
				3: {
					[ResourceType.EVENTIUM]: 50_000,
					[ResourceType.GEMS]: 400,
				},
				4: {
					[ResourceType.EVENTIUM]: 90_000,
					[ResourceType.GEMS]: 400,
				},
				5: {
					[ResourceType.EVENTIUM]: 160_000,
					[ResourceType.GEMS]: 400,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.HALLOWEEN,
		},
	],
	[
		EventUpgradeIds.ha_ignis_fatuus,
		{
			levelUnlock: 1,
			heading: "upgrade.ignis_fatuus.non_translatable",
			description: "upgrade.jack_o_lantern_drop_rate_description",
			descriptionReplace: {
				"{percent}": "2%",
			},
			replaceValue: 0.02,
			price: {
				1: { [ResourceType.GEMS]: 750 },
				2: { [ResourceType.GEMS]: 750 },
				3: { [ResourceType.GEMS]: 750 },
			},
			maxLevel: 3,
			eventType: EventCategory.HALLOWEEN,
		},
	],
	[
		EventUpgradeIds.ha_mischievous_ghost,
		{
			levelUnlock: 1,
			heading: "upgrade.mischievous_ghost",
			description: "upgrade.jack_o_lantern_reward_description",
			descriptionReplace: {
				"{number}": "40",
			},
			replaceValue: 40,
			price: {
				1: { [ResourceType.EVENTIUM]: 3_000 },
				2: { [ResourceType.EVENTIUM]: 6_000 },
				3: { [ResourceType.EVENTIUM]: 10_000 },
				4: { [ResourceType.EVENTIUM]: 20_000 },
				5: { [ResourceType.EVENTIUM]: 40_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.HALLOWEEN,
		},
	],
	[
		EventUpgradeIds.ha_troubled_ghost,
		{
			levelUnlock: 1,
			heading: "upgrade.troubled_ghost",
			description: "upgrade.jack_o_lantern_reward_description",
			descriptionReplace: {
				"{number}": "100",
			},
			replaceValue: 100,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 100_000,
					[ResourceType.GEMS]: 400,
				},
				2: {
					[ResourceType.EVENTIUM]: 150_000,
					[ResourceType.GEMS]: 400,
				},
				3: {
					[ResourceType.EVENTIUM]: 200_000,
					[ResourceType.GEMS]: 400,
				},
				4: {
					[ResourceType.EVENTIUM]: 250_000,
					[ResourceType.GEMS]: 400,
				},
				5: {
					[ResourceType.EVENTIUM]: 300_000,
					[ResourceType.GEMS]: 400,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.HALLOWEEN,
		},
	],
	[
		EventUpgradeIds.ha_evil_ghost,
		{
			levelUnlock: 1,
			heading: "upgrade.evil_ghost",
			description: "upgrade.jack_o_lantern_reward_description",
			descriptionReplace: {
				"{number}": "200",
			},
			replaceValue: 200,
			price: {
				1: { [ResourceType.GEMS]: 800 },
				2: { [ResourceType.GEMS]: 800 },
				3: { [ResourceType.GEMS]: 800 },
			},
			maxLevel: 3,
			eventType: EventCategory.HALLOWEEN,
		},
	],
	[
		EventUpgradeIds.ha_pumpkin_seeds,
		{
			levelUnlock: 1,
			heading: "upgrade.pumpkin_seeds",
			description: "upgrade.map_item_cap_description",
			descriptionReplace: {
				"{number}": "2",
			},
			replaceValue: 2,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 20_000,
					[ResourceType.GEMS]: 300,
				},
				2: {
					[ResourceType.EVENTIUM]: 40_000,
					[ResourceType.GEMS]: 300,
				},
				3: {
					[ResourceType.EVENTIUM]: 60_000,
					[ResourceType.GEMS]: 300,
				},
				4: {
					[ResourceType.EVENTIUM]: 90_000,
					[ResourceType.GEMS]: 300,
				},
				5: {
					[ResourceType.EVENTIUM]: 120_000,
					[ResourceType.GEMS]: 300,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.HALLOWEEN,
		},
	],
	[
		EventUpgradeIds.ha_pumpkin_patch,
		{
			levelUnlock: 1,
			heading: "upgrade.pumpkin_patch",
			description: "upgrade.map_item_cap_description",
			descriptionReplace: {
				"{number}": "5",
			},
			replaceValue: 5,
			price: {
				1: { [ResourceType.GEMS]: 500 },
				2: { [ResourceType.GEMS]: 500 },
			},
			maxLevel: 2,
			eventType: EventCategory.HALLOWEEN,
		},
	],
	[
		EventUpgradeIds.ha_pumpkin_magnet,
		{
			levelUnlock: 1,
			heading: "upgrade.pumpkin_magnet",
			description: "upgrade.map_item_distance_description",
			descriptionReplace: {
				"{number}": "100",
			},
			replaceValue: 100,
			price: {
				1: { [ResourceType.GEMS]: 150 },
			},
			maxLevel: 1,
			eventType: EventCategory.HALLOWEEN,
		},
	],
	[
		EventUpgradeIds.ha_trading,
		{
			levelUnlock: 1,
			heading: "upgrade.trading",
			description: "event.conversion_rate_description",
			descriptionReplace: {
				"{percent}": "2%",
			},
			replaceValue: 0.02,
			price: {
				1: { [ResourceType.EVENTIUM]: 100_000 },
				2: { [ResourceType.EVENTIUM]: 200_000 },
				3: { [ResourceType.EVENTIUM]: 300_000 },
				4: { [ResourceType.EVENTIUM]: 500_000 },
				5: { [ResourceType.EVENTIUM]: 1_000_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.HALLOWEEN,
		},
	],
	[
		EventUpgradeIds.ha_foreman,
		{
			levelUnlock: 1,
			heading: "upgrade.foreman",
			description: "upgrade.eventium_offline_income_description",
			descriptionReplace: {
				"{percent}": "100%",
			},
			replaceValue: 1,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 4_000_000,
					[ResourceType.GEMS]: 1_000,
				},
			},
			maxLevel: 1,
			eventType: EventCategory.HALLOWEEN,
		},
	],
	[
		EventUpgradeIds.ha_special1,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 30,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.HALLOWEEN,
			attributes: {
				value: 30,
				icon: "icon-extra-eventium-pumpkin-pickups",
			},
		},
	],
	[
		EventUpgradeIds.ha_special2,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 0.03,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.HALLOWEEN,
			attributes: {
				percentage: 3,
				icon: "icon-extra-chance-spawn-jackolantern-pickup",
			},
		},
	],
	[
		EventUpgradeIds.ha_special4,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 200,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.HALLOWEEN,
			attributes: {
				value: 200,
				icon: "icon-extra-eventium-jackolantern-pickups",
			},
		},
	],
	[
		EventUpgradeIds.ha_fire1,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 10,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.HALLOWEEN,
			attributes: {
				income: 10,
				icon: "icon-halloween-eventium-income",
			},
		},
	],
	[
		EventUpgradeIds.ha_fire2,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 20,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.HALLOWEEN,
			attributes: {
				income: 20,
				icon: "icon-halloween-eventium-income",
			},
		},
	],
	[
		EventUpgradeIds.ha_fire3,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 30,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.HALLOWEEN,
			attributes: {
				income: 30,
				icon: "icon-halloween-eventium-income",
			},
		},
	],
	[
		EventUpgradeIds.ha_fire4,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 50,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.HALLOWEEN,
			attributes: {
				income: 50,
				icon: "icon-halloween-eventium-income",
			},
		},
	],
	// -------- PIRATES UPGRADES START HERE --------
	[
		EventUpgradeIds.pi_coin_minting,
		{
			levelUnlock: 1,
			heading: "upgrade.coin_minting",
			description: "event.eventium_production_boost_flat",
			descriptionReplace: {
				"{number}": "4",
			},
			replaceValue: 4,
			price: {
				1: { [ResourceType.EVENTIUM]: 100 },
				2: { [ResourceType.EVENTIUM]: 200 },
				3: { [ResourceType.EVENTIUM]: 400 },
				4: { [ResourceType.EVENTIUM]: 800 },
				5: { [ResourceType.EVENTIUM]: 1_500 },
			},
			maxLevel: 5,
			eventType: EventCategory.PIRATES,
		},
	],
	[
		EventUpgradeIds.pi_small_mint,
		{
			levelUnlock: 1,
			heading: "upgrade.small_mint",
			description: "event.eventium_production_boost_flat",
			descriptionReplace: {
				"{number}": "8",
			},
			replaceValue: 8,
			price: {
				1: { [ResourceType.EVENTIUM]: 5_000 },
				2: { [ResourceType.EVENTIUM]: 10_000 },
				3: { [ResourceType.EVENTIUM]: 20_000 },
				4: { [ResourceType.EVENTIUM]: 40_000 },
				5: { [ResourceType.EVENTIUM]: 80_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.PIRATES,
		},
	],
	[
		EventUpgradeIds.pi_medium_mint,
		{
			levelUnlock: 1,
			heading: "upgrade.medium_mint",
			description: "event.eventium_production_boost_flat",
			descriptionReplace: {
				"{number}": "16",
			},
			replaceValue: 16,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 150_000,
					[ResourceType.GEMS]: 600,
				},
				2: {
					[ResourceType.EVENTIUM]: 300_000,
					[ResourceType.GEMS]: 600,
				},
				3: {
					[ResourceType.EVENTIUM]: 450_000,
					[ResourceType.GEMS]: 600,
				},
				4: {
					[ResourceType.EVENTIUM]: 600_000,
					[ResourceType.GEMS]: 600,
				},
				5: {
					[ResourceType.EVENTIUM]: 750_000,
					[ResourceType.GEMS]: 600,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.PIRATES,
		},
	],
	[
		EventUpgradeIds.pi_large_mint,
		{
			levelUnlock: 1,
			heading: "upgrade.large_mint",
			description: "event.eventium_production_boost_flat",
			descriptionReplace: {
				"{number}": "30",
			},
			replaceValue: 30,
			price: {
				1: { [ResourceType.GEMS]: 1_000 },
				2: { [ResourceType.GEMS]: 1_000 },
			},
			maxLevel: 2,
			eventType: EventCategory.PIRATES,
		},
	],
	[
		EventUpgradeIds.pi_town_maps,
		{
			levelUnlock: 1,
			heading: "upgrade.town_maps",
			description: "upgrade.pirates_map_reward_description",
			descriptionReplace: {
				"{number}": "4",
			},
			replaceValue: 4,
			price: {
				1: { [ResourceType.EVENTIUM]: 150 },
				2: { [ResourceType.EVENTIUM]: 300 },
				3: { [ResourceType.EVENTIUM]: 600 },
				4: { [ResourceType.EVENTIUM]: 1_000 },
				5: { [ResourceType.EVENTIUM]: 2_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.PIRATES,
		},
	],
	[
		EventUpgradeIds.pi_terrain_maps,
		{
			levelUnlock: 1,
			heading: "upgrade.terrain_maps",
			description: "upgrade.pirates_map_reward_description",
			descriptionReplace: {
				"{number}": "8",
			},
			replaceValue: 8,
			price: {
				1: { [ResourceType.EVENTIUM]: 4_000 },
				2: { [ResourceType.EVENTIUM]: 8_000 },
				3: { [ResourceType.EVENTIUM]: 15_000 },
				4: { [ResourceType.EVENTIUM]: 25_000 },
				5: { [ResourceType.EVENTIUM]: 50_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.PIRATES,
		},
	],
	[
		EventUpgradeIds.pi_detailed_maps,
		{
			levelUnlock: 1,
			heading: "upgrade.detailed_maps",
			description: "upgrade.pirates_map_reward_description",
			descriptionReplace: {
				"{number}": "16",
			},
			replaceValue: 16,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 75_000,
					[ResourceType.GEMS]: 400,
				},
				2: {
					[ResourceType.EVENTIUM]: 100_000,
					[ResourceType.GEMS]: 400,
				},
				3: {
					[ResourceType.EVENTIUM]: 150_000,
					[ResourceType.GEMS]: 400,
				},
				4: {
					[ResourceType.EVENTIUM]: 200_000,
					[ResourceType.GEMS]: 400,
				},
				5: {
					[ResourceType.EVENTIUM]: 300_000,
					[ResourceType.GEMS]: 400,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.PIRATES,
		},
	],
	[
		EventUpgradeIds.pi_atlas,
		{
			levelUnlock: 1,
			heading: "upgrade.atlas",
			description: "upgrade.pirates_map_reward_description",
			descriptionReplace: {
				"{number}": "30",
			},
			replaceValue: 30,
			price: {
				1: { [ResourceType.GEMS]: 600 },
				2: { [ResourceType.GEMS]: 600 },
				3: { [ResourceType.GEMS]: 600 },
			},
			maxLevel: 3,
			eventType: EventCategory.PIRATES,
		},
	],
	[
		EventUpgradeIds.pi_treasure_hunters,
		{
			levelUnlock: 1,
			heading: "upgrade.treasure_hunters",
			description: "upgrade.pirates_treasure_drop_rate_description",
			descriptionReplace: {
				"{percent}": "1%",
			},
			replaceValue: 0.01,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 15_000,
					[ResourceType.GEMS]: 400,
				},
				2: {
					[ResourceType.EVENTIUM]: 30_000,
					[ResourceType.GEMS]: 400,
				},
				3: {
					[ResourceType.EVENTIUM]: 50_000,
					[ResourceType.GEMS]: 400,
				},
				4: {
					[ResourceType.EVENTIUM]: 90_000,
					[ResourceType.GEMS]: 400,
				},
				5: {
					[ResourceType.EVENTIUM]: 160_000,
					[ResourceType.GEMS]: 400,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.PIRATES,
		},
	],
	[
		EventUpgradeIds.pi_experienced_treasure_hunters,
		{
			levelUnlock: 1,
			heading: "upgrade.experienced_treasure_hunters",
			description: "upgrade.pirates_treasure_drop_rate_description",
			descriptionReplace: {
				"{percent}": "2%",
			},
			replaceValue: 0.02,
			price: {
				1: { [ResourceType.GEMS]: 750 },
				2: { [ResourceType.GEMS]: 750 },
				3: { [ResourceType.GEMS]: 750 },
			},
			maxLevel: 3,
			eventType: EventCategory.PIRATES,
		},
	],
	[
		EventUpgradeIds.pi_buried_treasure,
		{
			levelUnlock: 1,
			heading: "upgrade.buried_treasure",
			description: "upgrade.pirates_treasure_reward_description",
			descriptionReplace: {
				"{number}": "40",
			},
			replaceValue: 40,
			price: {
				1: { [ResourceType.EVENTIUM]: 3_000 },
				2: { [ResourceType.EVENTIUM]: 6_000 },
				3: { [ResourceType.EVENTIUM]: 10_000 },
				4: { [ResourceType.EVENTIUM]: 20_000 },
				5: { [ResourceType.EVENTIUM]: 40_000 },
			},
			maxLevel: 5,
			eventType: EventCategory.PIRATES,
		},
	],
	[
		EventUpgradeIds.pi_lost_treasure,
		{
			levelUnlock: 1,
			heading: "upgrade.lost_treasure",
			description: "upgrade.pirates_treasure_reward_description",
			descriptionReplace: {
				"{number}": "100",
			},
			replaceValue: 100,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 100_000,
					[ResourceType.GEMS]: 400,
				},
				2: {
					[ResourceType.EVENTIUM]: 150_000,
					[ResourceType.GEMS]: 400,
				},
				3: {
					[ResourceType.EVENTIUM]: 200_000,
					[ResourceType.GEMS]: 400,
				},
				4: {
					[ResourceType.EVENTIUM]: 250_000,
					[ResourceType.GEMS]: 400,
				},
				5: {
					[ResourceType.EVENTIUM]: 300_000,
					[ResourceType.GEMS]: 400,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.PIRATES,
		},
	],
	[
		EventUpgradeIds.pi_cursed_treasure,
		{
			levelUnlock: 1,
			heading: "upgrade.cursed_treasure",
			description: "upgrade.pirates_treasure_reward_description",
			descriptionReplace: {
				"{number}": "200",
			},
			replaceValue: 200,
			price: {
				1: { [ResourceType.GEMS]: 800 },
				2: { [ResourceType.GEMS]: 800 },
				3: { [ResourceType.GEMS]: 800 },
			},
			maxLevel: 3,
			eventType: EventCategory.PIRATES,
		},
	],
	[
		EventUpgradeIds.pi_pirate_code,
		{
			levelUnlock: 1,
			heading: "upgrade.pirate_code",
			description: "upgrade.map_item_cap_description",
			descriptionReplace: {
				"{number}": "2",
			},
			replaceValue: 2,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 20_000,
					[ResourceType.GEMS]: 300,
				},
				2: {
					[ResourceType.EVENTIUM]: 40_000,
					[ResourceType.GEMS]: 300,
				},
				3: {
					[ResourceType.EVENTIUM]: 60_000,
					[ResourceType.GEMS]: 300,
				},
				4: {
					[ResourceType.EVENTIUM]: 90_000,
					[ResourceType.GEMS]: 300,
				},
				5: {
					[ResourceType.EVENTIUM]: 120_000,
					[ResourceType.GEMS]: 300,
				},
			},
			maxLevel: 5,
			eventType: EventCategory.PIRATES,
		},
	],
	[
		EventUpgradeIds.pi_rum_barrels,
		{
			levelUnlock: 1,
			heading: "upgrade.rum_barrels",
			description: "upgrade.map_item_cap_description",
			descriptionReplace: {
				"{number}": "5",
			},
			replaceValue: 5,
			price: {
				1: { [ResourceType.GEMS]: 500 },
				2: { [ResourceType.GEMS]: 500 },
			},
			maxLevel: 2,
			eventType: EventCategory.PIRATES,
		},
	],
	[
		EventUpgradeIds.pi_magnetic,
		{
			levelUnlock: 1,
			heading: "upgrade.magnet",
			description: "upgrade.map_item_distance_description",
			descriptionReplace: {
				"{number}": "100",
			},
			replaceValue: 100,
			price: {
				1: { [ResourceType.GEMS]: 150 },
			},
			maxLevel: 1,
			eventType: EventCategory.PIRATES,
		},
	],
	[
		EventUpgradeIds.pi_foreman,
		{
			levelUnlock: 1,
			heading: "upgrade.foreman",
			description: "upgrade.eventium_offline_income_description",
			descriptionReplace: {
				"{percent}": "100%",
			},
			replaceValue: 1,
			price: {
				1: {
					[ResourceType.EVENTIUM]: 4_000_000,
					[ResourceType.GEMS]: 1_000,
				},
			},
			maxLevel: 1,
			eventType: EventCategory.PIRATES,
		},
	],
	[
		EventUpgradeIds.pi_pack1,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 30,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.PIRATES,
			attributes: {
				value: 30,
				icon: "icon-extra-eventium-map-pickups",
			},
		},
	],
	[
		EventUpgradeIds.pi_pack2,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 0.03,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.PIRATES,
			attributes: {
				percentage: 3,
				icon: "icon-extra-chance-spawn-treasure-pickup",
			},
		},
	],
	[
		EventUpgradeIds.pi_pack4,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 200,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.PIRATES,
			attributes: {
				value: 200,
				icon: "icon-extra-eventium-treasure-pickups",
			},
		},
	],
	[
		EventUpgradeIds.pi_bundle1,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 10,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.PIRATES,
			attributes: {
				income: 10,
				icon: "icon-pirates-eventium-income",
			},
		},
	],
	[
		EventUpgradeIds.pi_bundle2,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 20,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.PIRATES,
			attributes: {
				income: 20,
				icon: "icon-pirates-eventium-income",
			},
		},
	],
	[
		EventUpgradeIds.pi_bundle3,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 30,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.PIRATES,
			attributes: {
				income: 30,
				icon: "icon-pirates-eventium-income",
			},
		},
	],
	[
		EventUpgradeIds.pi_bundle4,
		{
			levelUnlock: 1,
			heading: "",
			description: "",
			replaceValue: 50,
			price: {},
			maxLevel: 2,
			eventType: EventCategory.PIRATES,
			attributes: {
				income: 50,
				icon: "icon-pirates-eventium-income",
			},
		},
	],
]);

export const getCurrentEventUpgrades = (
	eventType: EventCategory | null,
): Map<EventUpgradeId, UpgradeItem> => {
	if (eventType === null) return new Map();
	return new Map(
		Array.from(eventUpgrades.entries()).filter(
			([, upgrade]) =>
				upgrade.eventType === eventType &&
				Object.keys(upgrade.price).length > 0,
		),
	);
};
